import "./manuscript.css";
import { useEffect, useRef, type ReactNode } from "react";
import { entityId, type LookView, type ThingView, type ThreadView } from "../../shared/story";
import { VOICE_NAMES } from "../../shared/voices";
import { Credit, TrustLinks, type Meta } from "../trust";
import { WhereYouAre } from "../noirmap";
import { Care, DeleteStory, Kept, Weaving, waiting, More, mentions, SoundToggle, ChapterEnd, DreamNow, HurryHint, frayOf, inkOf, type Ink, Plates, PlatesLink, SkinPicker, Unwoven, ReadingRoom, holdToHurry, Writer, quietFor, threadOrder, type HomeBits, type ThemeProps } from "../common";
import { Margins } from "../v2/margins";
import { PenLine, Pull } from "../v2/pens";
import { LookActions } from "../v2/pocket";
import type { Shown, StoryUI } from "../useStory";

/**
 * A · Manuscript with marginalia. The narrator is set in print on a page; the player's lines are written in by hand
 * between the printed lines. A cut reply stops mid-line and the handwriting starts on the same line. "Look closer"
 * becomes numbered footnotes; the margins carry the open threads, the tempo, and found objects (all from the world
 * graph, no model calls).
 */

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];
const chapterOf = (n: number) => ROMAN[Math.min(ROMAN.length - 1, Math.floor(Math.max(0, n - 1) / 10))]!;
const DYNAMICS = ["p", "mp", "mf", "f", "ff"];
const dyn = (t: number | undefined) => DYNAMICS[Math.max(0, Math.min(4, Math.round((t ?? 3) - 1)))]!;

interface Note { num: string; turn: number; look: LookView }

/** Footnotes in the order they were opened: 1, 2, …; a look at a child of note 1 is 1.1, 1.2, … */
function footnotes(looks: Record<number, LookView[]>): { notes: Note[]; numOf: Map<string, string> } {
  const notes: Note[] = [];
  const numOf = new Map<string, string>();
  const parentNum = new Map<string, string>();
  const subs = new Map<string, number>();
  let top = 0;
  for (const turn of Object.keys(looks).map(Number).sort((a, b) => a - b)) {
    for (const look of looks[turn] ?? []) {
      let num = numOf.get(look.thing);
      if (!num) {
        const parent = parentNum.get(look.thing);
        if (parent) {
          const k = (subs.get(parent) ?? 0) + 1;
          subs.set(parent, k);
          num = `${parent}.${k}`;
        } else {
          num = String(++top);
        }
        numOf.set(look.thing, num);
      }
      for (const ch of look.children) if (!parentNum.has(ch.id) && !numOf.has(ch.id)) parentNum.set(ch.id, num);
      notes.push({ num, turn, look });
    }
  }
  const key = (n: string) => n.split(".").map(Number);
  notes.sort((a, b) => {
    const x = key(a.num), y = key(b.num);
    for (let i = 0; i < Math.max(x.length, y.length); i++) if ((x[i] ?? -1) !== (y[i] ?? -1)) return (x[i] ?? -1) - (y[i] ?? -1);
    return 0;
  });
  return { notes, numOf };
}

/** Like common.linkify, but a name that already has a footnote carries its number. */
function inked(text: string, things: readonly ThingView[], onLook: ((name: string) => void) | null, numOf: Map<string, string>, ink: (name: string) => Ink): ReactNode[] {
  if (!onLook) return [text];
  const names = things.map((t) => t.name.replace(/^(the|a|an)\s+/i, "")).filter((n) => n.length > 2).sort((a, b) => b.length - a.length);
  if (!names.length) return [text];
  const re = new RegExp(`\\b(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`, "gi");
  const out: ReactNode[] = [];
  const seen = new Set<string>();
  let last = 0;
  for (const m of text.matchAll(re)) {
    const key = m[0].toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text.slice(last, m.index));
    const num = numOf.get(entityId(m[0]));
    out.push(
      <span key={m.index} className="ms-ref" {...(num ? { id: `ms-ref-${num}` } : {})}>
        <button type="button" className={`lm-thing ms-thing lm-ink-${num ? "live" : ink(m[0])}`} onClick={() => onLook(m[0])}>{m[0]}</button>
        {num && <sup>{num}</sup>}
      </span>,
    );
    last = m.index! + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

const paragraphs = (prose: string) => prose.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

/** The running text: print and handwriting interleaved, a cut reply continuing in ink on the same line. */
function Text({ s, numOf }: { s: StoryUI; numOf: Map<string, string> }) {
  const things = s.view?.things ?? [];
  type Block = { key: string; kind: "print" | "hand" | "aside" | "plate" | "note" | "chapter" | "decision" | "scene" | "margins"; nodes: ReactNode[]; n?: number; live?: boolean; /** The turn a printed paragraph belongs to (V2: the page marks the newest). */ pn?: number };
  const blocks: Block[] = [];
  const all: (Shown & { live?: boolean })[] = [...s.turns];
  if (s.live) all.push({ n: s.live.n, input: s.live.input, prose: s.live.text, status: "streaming", intent: s.live.intent, live: true, ...(s.live.callback ? { callback: s.live.callback } : {}) });
  let prevCut = false;
  for (const t of all) {
    const meta = t.intent === "meta";
    const hand = <span className="ms-hand" key={`h${t.n}`}><PenLine input={t.input} /></span>;
    const lastPrint = blocks.at(-1);
    if (meta) {
      blocks.push({ key: `a${t.n}`, kind: "aside", nodes: [<span key="to" className="ms-aside-to">to the printer: </span>, hand] });
    } else if (prevCut && lastPrint?.kind === "print") {
      lastPrint.nodes.push(" ", hand);
    } else {
      if (t.verb === "elsewhere") blocks.push({ key: `s${t.n}`, kind: "scene", nodes: ["⁂"] });
      blocks.push({ key: `h${t.n}`, kind: "hand", nodes: [<span key="m" className="ms-caret-mark" aria-hidden="true">‸ </span>, hand, <Kept key="k" t={t} className="ms-kept" />], n: t.n });
    }
    if (t.voiceShift) blocks.push({ key: `v${t.n}`, kind: "note", nodes: [`The telling turns ${VOICE_NAMES[t.voiceShift.to as keyof typeof VOICE_NAMES] ?? t.voiceShift.to}.`] });
    if (t.decision) blocks.push({ key: `d${t.n}`, kind: "decision", nodes: ["The moment of decision"] });
    if (t.callback && mentions(t.prose, t.callback)) blocks.push({ key: `r${t.n}`, kind: "note", nodes: [`↺ ${t.callback}, which the story let go quiet, comes back`], live: !!t.live });
    const paras = paragraphs(t.prose);
    const onLook = t.live ? null : (name: string) => s.look(t.n, name);
    if (meta) {
      blocks.push({ key: `p${t.n}`, kind: "aside", nodes: [<span key="pr" className="ms-aside-to">the printer: </span>, t.prose], live: !!t.live });
    } else if (t.live && !paras.length) {
      blocks.push({ key: `p${t.n}-0`, kind: "print", nodes: [], n: t.n, pn: t.n, live: true });
    } else {
      paras.forEach((p, i) => blocks.push({
        key: `p${t.n}-${i}`, kind: "print", live: !!t.live, pn: t.n, ...(i === 0 ? { n: t.n } : {}),
        nodes: [...inked(p, things, onLook, numOf, inkOf(s.view, t.n)),
          ...(i === paras.length - 1 && t.status === "cut"
            ? [<Unwoven key="uw" s={s} t={t} className="ms-struck" label={(k) => ` ${k} ${k === 1 ? "word" : "words"} struck from the press`} />] : [])],
      }));
    }
    // V2: what drifted into the margins beside this reply, set as a slip under it.
    if (!t.live && t.intent !== "meta") blocks.push({ key: `mg${t.n}`, kind: "margins", nodes: [<Pull key="pl" s={s} n={t.n} className="ms-pull" />, <Margins key="mg" s={s} n={t.n} className="ms-margins" />] });
    if (t.chapterEnd) blocks.push({ key: `ce${t.n}`, kind: "chapter", nodes: [<ChapterEnd key="ce" s={s} t={t} className="ms-chapter" />] });
    prevCut = t.status === "cut";
    if (!t.live && s.view?.images.some((i) => i.at === t.n)) {
      blocks.push({ key: `plate${t.n}`, kind: "plate", nodes: [<Plates key="pl" s={s} at={t.n} className="ms-plate" />] });
      prevCut = false;
    }
  }
  // The live reply's paragraphs share one wrapper (data-live) so its text can be followed as one piece.
  const liveFrom = blocks.findIndex((b) => b.live);
  const render = (b: Block, i: number, arr: Block[]) => {
    const isLast = i === arr.length - 1;
    const tail = b.live && isLast && s.live
      ? s.live.streaming
        ? <span className="ms-cursor" aria-hidden="true" />
        : s.live.paused ? <span className="ms-stops"> the press stops while you write</span> : null
      : null;
    if (b.kind === "plate" || b.kind === "chapter" || b.kind === "margins") return <div key={b.key}>{b.nodes}</div>;
    if (b.kind === "hand") return <p key={b.key} className="ms-hand-line" data-turn={b.n}>{b.nodes}</p>;
    if (b.kind === "scene") return <p key={b.key} className="lm-scene-break ms-scene" aria-hidden="true">{b.nodes}</p>;
    if (b.kind === "decision") return <p key={b.key} className="lm-decision ms-decision">{b.nodes}</p>;
    if (b.kind === "note") return <p key={b.key} className="ms-returned lm-returned">{b.nodes}</p>;
    if (b.kind === "aside") return <p key={b.key} className="ms-aside">{b.nodes}{tail}</p>;
    return (
      <p key={b.key} className="ms-print" data-prose={b.pn}>
        {b.n !== undefined && <span className="ms-pnum" aria-hidden="true">{b.n}</span>}
        {b.nodes}{tail}
      </p>
    );
  };
  const before = liveFrom < 0 ? blocks : blocks.slice(0, liveFrom);
  const liveBlocks = liveFrom < 0 ? [] : blocks.slice(liveFrom);
  return (
    <>
      {before.map((b, i) => render(b, i, liveFrom < 0 ? before : blocks))}
      {liveBlocks.length > 0 && <div data-live aria-live="polite" {...holdToHurry(s)}>{liveBlocks.map((b, i) => render(b, i, liveBlocks))}</div>}
      {liveBlocks.length > 0 && waiting(s) && <Weaving label="the press is setting the next lines" className="ms-weaving" />}
      {liveBlocks.length > 0 && <HurryHint s={s} className="ms-stops" />}
      {s.pending && (
        <>
          <p className="ms-hand-line" data-pending><span className="ms-caret-mark" aria-hidden="true">‸ </span><span className="ms-hand">{s.pending}</span></p>
          <Weaving label="the press is setting the next lines" className="ms-weaving" />
        </>
      )}
    </>
  );
}

function Footnotes({ s, notes, numOf }: { s: StoryUI; notes: Note[]; numOf: Map<string, string> }) {
  // A note just opened: bring it into view (on a phone it lands far below the word you tapped).
  const count = useRef(notes.length);
  useEffect(() => {
    if (notes.length > count.current) {
      const latest = [...numOf.values()].at(-1);
      document.getElementById(`ms-fn-${latest}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    count.current = notes.length;
  }, [notes.length, numOf]);
  if (!notes.length) return null;
  return (
    <section className="ms-notes" aria-label="Footnotes">
      {notes.map(({ num, turn, look }) => (
        <div key={`${num}-${look.thing}`} id={`ms-fn-${num}`} className={`ms-note ${num.includes(".") ? "ms-subnote" : ""}`}>
          <p>
            <span className="ms-note-num">{num}</span> <i>{look.thing}.</i> {look.description}{" "}
            <button type="button" className="ms-back" aria-label={`Back to the text of note ${num}`}
              onClick={() => document.getElementById(`ms-ref-${num}`)?.scrollIntoView({ block: "center", behavior: "smooth" })}>↩</button>
          </p>
          <p className="ms-note-kids">
            <span className="ms-kids-label">Look closer at:</span>{" "}
            {look.children.map((ch) => {
              const n = numOf.get(ch.id);
              return (
                <button key={ch.id} type="button" className="lm-thing ms-kid" title={ch.hint} onClick={() => s.look(turn, ch.name)}>
                  {n ? `${n} ` : ""}{ch.name}
                </button>
              );
            })}
          </p>
          <LookActions s={s} thing={look.thing} className="ms-note-acts" />
        </div>
      ))}
    </section>
  );
}

function Threads({ s, threads, now }: { s: StoryUI; threads: readonly ThreadView[]; now: number }) {
  const list = threadOrder(threads).slice(0, 8);
  if (!list.length) return <p className="ms-quiet">No threads yet. They appear as the story owes you answers.</p>;
  return (
    <ul className="ms-threads">
      {list.map((t) => {
        const q = quietFor(t, now);
        return (
          <li key={t.id} className={t.state === "closed" ? "ms-closed" : q === 0 ? "ms-sounding" : ""} title={t.hint}>
            {t.state === "open" && s.id
              ? <button type="button" className="ms-thread-name lm-pull" data-fray={frayOf(t, now)} onClick={() => s.pull(t)} disabled={s.busy} title={`Pull this thread: ${t.hint}`}>{t.name}</button>
              : <span className="ms-thread-name">{t.name}</span>}
            <span className="ms-thread-when">since ¶{t.turn} · {t.state === "closed" ? "resolved" : q === 0 ? "sounding now" : `quiet for ${q}`}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Tempo({ s }: { s: StoryUI }) {
  const tensions = [...s.turns.map((t) => t.tension), s.live?.tension].filter((t): t is number => typeof t === "number");
  if (!tensions.length) return null;
  const now = tensions.at(-1)!;
  const before = tensions.at(-2);
  const a = before === undefined ? null : dyn(before);
  const b = dyn(now);
  const lean = before === undefined || Math.round(now) === Math.round(before) ? "the story holds its breath"
    : now > before ? "the story is leaning forward" : "the story eases";
  return (
    <div className="ms-tempo">
      <span className="ms-label">Tempo</span>
      <span className="ms-dyn">{a && a !== b ? <>{a} <span aria-hidden="true">⟶</span> {b}</> : b}</span>
      <span className="ms-lean">{lean}</span>
    </div>
  );
}

function TitlePage({ s, home, openPlates, meta }: { s: StoryUI; home: HomeBits; openPlates(): void; meta(m: Meta): void }) {
  return (
    <section className="ms-title-page">
      <p className="ms-imprint">a novel in progress</p>
      <h1 className="ms-book-title">Weftwake</h1>
      <p className="ms-lede">A story that isn't written yet. Write its first line by hand; the press sets the rest, and you may stop it mid-sentence to write in your own.</p>
      <p className="lm-what">Weftwake is a text adventure that writes itself as you play: you write what you do, an AI narrator writes what happens. Your first 40 turns are free.</p>
      <p className="ms-label">Begin with one of these</p>
      <div className="ms-seeds">
        {home.seeds.map((o) => (
          <button key={o} type="button" className="ms-seed" onClick={() => s.send(o)} disabled={s.busy}>‸ {o}</button>
        ))}
      </div>
      {home.stories.length > 0 && (
        <nav className="ms-contents" aria-label="Your stories">
          <p className="ms-label">Contents</p>
          <ol>
            {home.stories.map((st) => (
              <li key={st.id}>
                <a href={`/s/${st.id}`} onClick={(e) => { e.preventDefault(); home.open(st.id); }}>
                  <span className="ms-ct">{st.title}</span>
                  <span className="ms-leader" aria-hidden="true" />
                  <span className="ms-cn">{st.n}</span>
                </a>
                <DeleteStory title={st.title} remove={() => home.remove(st.id)} />
              </li>
            ))}
          </ol>
          <p className="ms-label"><PlatesLink open={openPlates} label="Plates" /></p>
        </nav>
      )}
    </section>
  );
}

export function ManuscriptTheme({ s, home, skin, setSkin, goHome, openPlates, meta }: ThemeProps) {
  const view = s.view;
  const things = view?.things ?? [];
  const threads = view?.threads ?? [];
  const latest = s.live?.n ?? s.turns.at(-1)?.n ?? view?.n ?? 0;
  const { notes, numOf } = footnotes(s.looks);
  const showRecap = !!view?.recap && s.turns.length > 0 && s.turns[0]!.n > 1;
  const hasMargins = !home && (threads.length > 0 || things.length > 0);

  return (
    <main className="sk-manuscript">
      <div className={`ms-desk ${home ? "ms-desk-home" : ""}`}>
        {!home && (
          <aside className="ms-margin ms-left" aria-label="Threads still open">
            <p className="ms-label">Threads still open</p>
            <p className="ms-hint">The story's loose ends. Tap one to bring it back on your next line.</p>
            <Threads s={s} threads={threads} now={latest} />
            <Tempo s={s} />
          </aside>
        )}

        <article className="ms-page">
          <div className="ms-topbar">
            {!home && s.id === null && <span className="ms-home">Weftwake</span>}
            <SoundToggle className="ms-skins-sound" />
            <SkinPicker skin={skin} setSkin={setSkin} className="ms-skins" />
          </div>

          {home ? <TitlePage s={s} home={home} openPlates={openPlates} meta={meta} /> : (
            <>
              <header className="ms-runhead">
                <a href="/" aria-label={s.id ? "Your stories" : undefined} onClick={(e) => { e.preventDefault(); if (s.id) meta("stories"); else goHome(); }}>Weftwake{s.id && <span className="lm-brand-caret" aria-hidden="true"> ▾</span>}</a>
                <span className="ms-runtitle">{chapterOf(latest)} · {view?.title ?? (s.id ? "" : "a new story")}</span>
                <span>{latest || ""}</span>
              </header>
              {showRecap && <p className="ms-recap"><span className="ms-label">Previously</span> {view!.recap}</p>}
              {s.loadFailed && (
                <p className="ms-erratum">Erratum: this story wouldn't open. <button type="button" onClick={s.reload}>Try again</button></p>
              )}
              <div className="ms-text" role="log" aria-label="The story" aria-busy={!!s.live?.streaming || !!s.pending}>
                <Text s={s} numOf={numOf} />
                {/* The room the reply writes into (common.tsx ReadingRoom): nothing on screen moves while it's written. */}
                {s.id && <ReadingRoom s={s} writer=".ms-writer" />}
              </div>
              <Footnotes s={s} notes={notes} numOf={numOf} />
              {hasMargins && (
                <section className="ms-marginalia" aria-label="In the margins">
                  <p className="ms-label">In the margins</p>
                  <Threads s={s} threads={threads} now={latest} />
                  <Tempo s={s} />
                  <WhereYouAre view={view!} onLook={(name) => s.look(latest, name)} className="ms-where" />
                </section>
              )}
            </>
          )}

          {s.notice && <p className="ms-erratum" role="status">{s.notice}</p>}
          <Care s={s} className="ms-care" />
          {!s.resources && (
            <Writer
              s={s}
              className="ms-writer"
              placeholder={s.live?.streaming ? "write to stop the press…" : s.id ? "write in the margin…" : "write the first line…"}
              sendLabel={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 19l7-7-4-4-7 7-2 6z" /><path d="M15 8l-6 6" />
                </svg>
              }
            />
          )}
          {/* On the title page the links and credit come after the line you write on, not between it and the openings. */}
          {home && (
            <>
              <p className="ms-label ms-home-foot"><TrustLinks meta={meta} /></p>
              <Credit className="ms-credit" />
            </>
          )}
          {s.id && (
            <footer className="ms-foot">
              <PlatesLink open={openPlates} label="plates" />
              <button type="button" onClick={() => meta("stories")}>stories</button>
              <button type="button" onClick={() => meta("map")}>journal</button>
              <button type="button" onClick={() => meta("cloth")}>the cloth</button>
              <button type="button" onClick={() => meta("account")}>account</button>
              <button type="button" onClick={() => meta("feedback")}>feedback</button>
              <More>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>privacy</a>
                <a href="/api/export">export</a>
                <button type="button" onClick={() => meta("tour")}>how to play</button>
                <DreamNow s={s} />
                <button type="button" onClick={s.remove}>delete this story</button>
              </More>
            </footer>
          )}
        </article>

        {!home && (
          <aside className="ms-margin ms-right" aria-label="Where you are">
            {view ? <WhereYouAre view={view} onLook={(name) => s.look(latest, name)} className="ms-where" /> : null}
          </aside>
        )}
      </div>
    </main>
  );
}
